import { Calendar, Check, CheckCircle2, ClipboardList, Clock } from 'lucide-react';
import { AddToCalendar } from 'components/Appointment/AddToCalendar';
import { ConfirmationBadge } from 'components/Appointment/ConfirmationBadge';
import { getConfirmationState } from 'components/Appointment/helper';
import { formatDate, timeAgo } from 'utils/helpers';
import { BADGE, CARD, LABEL, ROW, ROW_TITLE } from './helper';

const NextAppointment = ({ appointment, formsCompleted, confirming, onConfirm }) => {
  if (!appointment) {
    return (
      <div className={`${CARD} py-10 flex flex-col items-center text-center`}>
        <div className="w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center mb-2">
          <Calendar size={16} className="text-secondary-400" />
        </div>
        <p className="text-sm font-semibold text-secondary-700">No upcoming appointments</p>
        <p className="text-xs text-secondary-500 mt-1">Schedule a visit to see it here.</p>
      </div>
    );
  }

  const confirmationState = getConfirmationState(appointment);
  const forms = formsCompleted
    ? { label: 'Completed', icon: CheckCircle2, iconClass: 'text-green-600', badgeClass: 'bg-green-50 text-green-700' }
    : { label: 'Pending', icon: Clock, iconClass: 'text-amber-500', badgeClass: 'bg-amber-50 text-amber-700' };
  const FormsIcon = forms.icon;

  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="bg-gradient-to-br from-teamcare-blue to-teamcare-blue-dark text-white p-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest bg-white/15 rounded-full px-2 py-0.5">
                Next Appointment
              </span>
              <span className="text-[11px] text-white/70">{timeAgo(appointment.appointmentAt)}</span>
            </div>

            <h2 className="text-xl font-bold mt-2">{formatDate(appointment.date, 'ddd, MMMM D')}</h2>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] bg-white/15 rounded-full px-2.5 py-1">
                <Clock size={12} className="text-white/70" />
                <span className="font-semibold">{appointment.startTime}</span>
                <span className="text-white/70">· {appointment.durationMinutes} min</span>
              </span>
              {appointment.provider.name && (
                <span className="text-[11px] text-white/70">
                  {appointment.provider.name}
                  {appointment.provider.role && ` · ${appointment.provider.role}`}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end shrink-0">
            <AddToCalendar appointment={appointment} variant="ghost" label="Add to calendar" hideIcon />

            {confirmationState === 'pending' ? (
              <button
                onClick={onConfirm}
                disabled={confirming}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-800 bg-green-100 rounded-md px-2.5 py-1 shadow-md hover:bg-green-200 transition-colors disabled:opacity-60 disabled:cursor-wait animate-bounce motion-reduce:animate-none"
              >
                <Check size={12} />
                Confirm Your Appointment
              </button>
            ) : (
              <ConfirmationBadge appt={appointment} variant="pill" />
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
        <div className="flex items-center gap-3 p-3.5">
          <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
            <ClipboardList size={16} className="text-green-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-secondary-900">Treatment plan</p>
            <p className="text-[11px] text-secondary-400 mt-0.5">No treatment plan attached to this visit.</p>
          </div>
        </div>

        <div className="p-3.5">
          <p className={LABEL}>Before your appointment</p>

          <div className={`${ROW} mt-2.5`}>
            <p className={ROW_TITLE}>
              Appointment confirmation
            </p>
            <span className="shrink-0">
              <ConfirmationBadge appt={appointment} className={BADGE} iconSize={11} />
            </span>
          </div>

          <div className={`${ROW} mt-2`}>
            <p className={ROW_TITLE}>
              Patient forms
            </p>

            <span className={`inline-flex shrink-0 items-center gap-1 font-semibold uppercase rounded-md ${forms.badgeClass} ${BADGE}`}>
              <FormsIcon size={11} />
              {forms.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NextAppointment;
